import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  IQuestionBankState,
  IQuestionBankData,
  IQuestionBankSection,
  IQuestionBankQuestion,
} from 'interfaces/questionBank'
import { RootState } from '..'
import {
  createEmptyQuestionBankSection,
  getUntitledSectionName,
  initialQuestionBankState,
} from './initialData'

const buildQuestionCode = (code: number) => `Q${String(code).padStart(3, '0')}`

const initialState: IQuestionBankState = initialQuestionBankState

const questionBankSlice = createSlice({
  name: 'questionBank',
  initialState,
  reducers: {
    setStage: (state, action: PayloadAction<number>) => {
      state.stage = action.payload
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload
    },
    setData: (state, action: PayloadAction<IQuestionBankData>) => {
      state.data = action.payload
      state.updated = false
    },
    setPrefill: (state, action: PayloadAction<IQuestionBankData>) => {
      state.pendingPrefill = action.payload
    },
    clearPrefill: (state) => {
      state.pendingPrefill = null
    },
    setName: (state, action: PayloadAction<string>) => {
      state.updated = true
      state.data.name = action.payload
    },
    setDescription: (state, action: PayloadAction<string>) => {
      state.updated = true
      state.data.description = action.payload
    },
    setMinimumPassingScore: (state, action: PayloadAction<number | ''>) => {
      state.updated = true
      state.data.minimumPassingScore = action.payload
    },
    setMaximumEvaluationAttempts: (
      state,
      action: PayloadAction<number | ''>
    ) => {
      state.updated = true
      state.data.maxRetries = action.payload
    },
    setEnableMultipleRetries: (state, action: PayloadAction<boolean>) => {
      state.updated = true
      state.data.multipleRetries = action.payload
    },
    setMandatoryEvaluation: (state, action: PayloadAction<boolean>) => {
      state.updated = true
      state.data.mandatory = action.payload
      if (action.payload) {
        state.data.approvalRequired = true
      } else {
        state.data.approvalRequired = false
      }
    },
    setApprovalRequired: (state, action: PayloadAction<boolean>) => {
      state.updated = true
      state.data.approvalRequired = action.payload
    },
    setAllowReviewOfResults: (state, action: PayloadAction<boolean>) => {
      state.updated = true
      state.data.showResults = action.payload
    },
    setSectionDescription: (
      state,
      action: PayloadAction<{ sectionOrder: number; description: string }>
    ) => {
      state.updated = true
      state.data.sections.forEach((section) => {
        if (section.order === action.payload.sectionOrder) {
          section.description = action.payload.description
        }
      })
    },
    addEmptySection: (state) => {
      state.updated = true
      state.data.sections = [
        ...state.data.sections,
        createEmptyQuestionBankSection(state.data.sections.length),
      ]
    },
    removeSection: (state, action: PayloadAction<number>) => {
      state.updated = true
      const sections = state.data.sections.filter(
        (section) => section.order !== action.payload
      )
      if (sections.length > 0) {
        state.data.sections = sections.map((section, index) => {
          section.order = index
          return section
        })
      } else {
        state.data.sections = [createEmptyQuestionBankSection()]
      }
    },
    changeSectionName: (
      state,
      action: PayloadAction<{ sectionOrder: number; sectionName: string }>
    ) => {
      state.updated = true
      state.data.sections.forEach((section) => {
        if (section.order === action.payload.sectionOrder) {
          section.name = action.payload.sectionName
        }
      })
    },
    validateSectionName: (state, action: PayloadAction<number>) => {
      state.updated = true
      state.data.sections.forEach((section) => {
        if (section.order === action.payload) {
          if (section.name.trim().length === 0) {
            section.name = getUntitledSectionName()
          }
        }
      })
    },
    updateSectionsOrder: (
      state,
      action: PayloadAction<IQuestionBankSection[]>
    ) => {
      state.updated = true
      state.data.sections = action.payload.map((section, index) => {
        return {
          ...section,
          order: index,
        }
      })
    },
    updateQuestionsOrder: (
      state,
      action: PayloadAction<{
        sectionOrder: number
        questions: IQuestionBankQuestion[]
      }>
    ) => {
      state.updated = true
      for (const section of state.data.sections) {
        if (section.order === action.payload.sectionOrder) {
          section.questions = action.payload.questions.map(
            (question, index) => {
              return {
                ...question,
                order: index,
              }
            }
          )
          break
        }
      }
    },
    changeQuestionScore: (
      state,
      action: PayloadAction<{
        sectionOrder: number
        questionOrder: number
        score: number | ''
      }>
    ) => {
      state.updated = true
      state.data.sections.forEach((section) => {
        if (section.order === action.payload.sectionOrder) {
          section.questions.forEach((question) => {
            if (question.order === action.payload.questionOrder) {
              question.score = action.payload.score
            }
          })
        }
      })
    },
    removeQuestion: (
      state,
      action: PayloadAction<{ sectionOrder: number; questionOrder: number }>
    ) => {
      state.updated = true
      state.data.sections.forEach((section) => {
        if (section.order === action.payload.sectionOrder) {
          section.questions = section.questions.filter(
            (question) => question.order !== action.payload.questionOrder
          )
        }
      })
    },
    addQuestion: (
      state,
      action: PayloadAction<{
        sectionOrder: number
        question: IQuestionBankQuestion
      }>
    ) => {
      state.updated = true

      const sections = state.data.sections
      const allCodes = sections.flatMap((section) =>
        section.questions.map((question) => question.code)
      )

      let maxQuestion =
        sections.reduce(
          (max, section) => Math.max(max, section.questions.length),
          0
        ) + 1

      let newCode = buildQuestionCode(maxQuestion)
      while (allCodes.includes(newCode)) {
        maxQuestion++
        newCode = buildQuestionCode(maxQuestion)
      }

      sections.forEach((section) => {
        if (section.order === action.payload.sectionOrder) {
          section.questions = [
            ...section.questions,
            {
              ...action.payload.question,
              order: section.questions.length,
              code: newCode,
            },
          ]
        }
      })
    },
    editQuestion: (
      state,
      action: PayloadAction<{
        sectionOrder: number
        question: IQuestionBankQuestion
      }>
    ) => {
      state.updated = true
      state.data.sections.forEach((section) => {
        if (section.order === action.payload.sectionOrder) {
          section.questions = section.questions.map((question) => {
            if (question.order === action.payload.question.order) {
              return action.payload.question
            }
            return question
          })
        }
      })
    },
    updateSection: (
      state,
      action: PayloadAction<{ section: IQuestionBankSection }>
    ) => {
      state.updated = true
      state.data.sections = state.data.sections.map((section) => {
        if (section.order === action.payload.section.order) {
          return action.payload.section
        }
        return section
      })
    },
  },
})

const {
  setStage,
  setData,
  setPrefill,
  clearPrefill,
  setLoading,
  setName,
  setDescription,
  setMinimumPassingScore,
  setMaximumEvaluationAttempts,
  setEnableMultipleRetries: setEnableAttemptLimit,
  setMandatoryEvaluation,
  setApprovalRequired,
  setAllowReviewOfResults,
  setSectionDescription,
  addEmptySection,
  removeSection,
  changeSectionName,
  validateSectionName,
  updateSectionsOrder,
  updateQuestionsOrder,
  changeQuestionScore,
  removeQuestion,
  addQuestion,
  editQuestion,
  updateSection,
} = questionBankSlice.actions
const stage = (state: RootState) => state.questionBank.stage
const data = (state: RootState) => state.questionBank.data
const loading = (state: RootState) => state.questionBank.loading
const updated = (state: RootState) => state.questionBank.updated
const pendingPrefill = (state: RootState) => state.questionBank.pendingPrefill

export const questionBank = {
  setStage,
  setData,
  setPrefill,
  clearPrefill,
  setLoading,
  setName,
  setDescription,
  setMinimumPassingScore,
  setMaximumEvaluationAttempts,
  setEnableAttemptLimit,
  setMandatoryEvaluation,
  setApprovalRequired,
  setAllowReviewOfResults,
  setSectionDescription,
  addEmptySection,
  removeSection,
  changeSectionName,
  validateSectionName,
  updateSectionsOrder,
  updateQuestionsOrder,
  changeQuestionScore,
  removeQuestion,
  addQuestion,
  editQuestion,
  updateSection,
  stage,
  data,
  loading,
  updated,
  pendingPrefill,
}

export default questionBankSlice.reducer
